import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import SearchBar, { type SearchFilters } from "@/components/SearchBar";
import { CategoryIcons } from "@/components/CategoryIcons";
import { RecommendationCarousel } from "@/components/RecommendationCarousel";
import { TrustSections } from "@/components/TrustSections";
import { FAQAccordion } from "@/components/FAQAccordion";
import { HostCTASection } from "@/components/HostCTASection";
import { Footer } from "@/components/Footer";
import { useHomeData } from "@/lib/useHomeData";
import { faqItems } from "@/data/mockData";

const Index = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [vetQuickCity, setVetQuickCity] = useState("");
  const [vetQuickWhen, setVetQuickWhen] = useState<"hoje" | "agendar">("hoje");
  const [vetQuickDate, setVetQuickDate] = useState("");
  const { data, err } = useHomeData();

  const handleSearch = (filters: SearchFilters) => {
    const params = new URLSearchParams();

    if (filters.cidade) params.set("cidade", filters.cidade);
    if (selectedCategory) params.set("service", selectedCategory);

    const qtd =
      (filters as any)?.petCount ??
      (filters as any)?.quantidade_pets ??
      (filters as any)?.petCountValue ??
      1;

    params.set("quantidade_pets", String(qtd));

    const today = new Date().toISOString().slice(0, 10);

    const entrada =
      filters.dateRange?.from?.toISOString().slice(0, 10) ??
      (filters as any)?.entrada ??
      today;

    params.set("entrada", entrada);

    const saida =
      filters.dateRange?.to?.toISOString().slice(0, 10) ??
      (filters as any)?.saida ??
      "";

    if (saida) params.set("saida", saida);

    navigate(`/buscar?${params.toString()}`);
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory((prev) => (prev === category ? "" : category));
  };

  const cidades = (data?.items ?? [])
    .map((item: any) =>
      typeof item === "string" ? item : item?.label || item?.cidade || ""
    )
    .filter(Boolean);

  const servicos = data?.servicos ?? [];
  const buscarVeterinario = () => {
    const hoje = new Date();
    const dataLocal = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}-${String(hoje.getDate()).padStart(2, "0")}`;
    const params = new URLSearchParams({ service: "veterinario", entrada: vetQuickWhen === "agendar" && vetQuickDate ? vetQuickDate : dataLocal });
    if (vetQuickCity.trim()) params.set("cidade", vetQuickCity.trim());
    navigate(`/buscar?${params.toString()}`);
  };

  const recomendacoes = (
    (data?.carousel?.length ?? 0) > 0
      ? data?.carousel
      : (data?.recomendados?.length ?? 0) > 0
      ? data?.recomendados
      : []
  ) ?? [];

  return (
    <div className="min-h-screen bg-background">
      <Header activeTab="tutores" />

      <section className="gradient-hero py-8 md:py-12 lg:py-16">
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center mb-6 md:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground mb-3 md:mb-4 animate-fade-in-up leading-tight">
              Encontre o lar perfeito para seu pet
            </h1>

            <p
              className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto animate-fade-in-up leading-relaxed px-2 sm:px-0"
              style={{ animationDelay: "0.1s" }}
            >
              Hospedagem, creche e cuidados com anfitriões verificados perto de você.
            </p>
          </div>

          <div className="mb-5 md:mb-6">
            <CategoryIcons
              selected={selectedCategory}
              onSelect={handleCategorySelect}
            />
          </div>

          <div className="mt-4 md:mt-6 w-full">
            <div className="w-full">
              {selectedCategory === "veterinario" ? (
                <section aria-label="Buscar atendimento veterinário" className="mx-auto max-w-3xl rounded-2xl bg-background border p-4 sm:p-6 shadow-sm text-left space-y-4">
                  <div>
                    <h2 className="text-xl font-bold">Precisa de um veterinário?</h2>
                    <p className="text-sm text-muted-foreground">Encontre profissionais que oferecem atendimento veterinário na sua região.</p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="text-sm font-semibold">Cidade
                      <input list="vet-cidades" value={vetQuickCity} onChange={(e) => setVetQuickCity(e.target.value)} placeholder="Digite sua cidade" className="mt-1 w-full rounded-lg border bg-background px-3 py-3 font-normal" />
                      <datalist id="vet-cidades">{cidades.map((cidade) => <option key={cidade} value={cidade} />)}</datalist>
                    </label>
                    <div className="text-sm font-semibold">Quando precisa?
                      <div className="mt-1 flex gap-2">
                        <button type="button" onClick={() => setVetQuickWhen("hoje")} aria-pressed={vetQuickWhen === "hoje"} className={`flex-1 rounded-lg border px-3 py-3 ${vetQuickWhen === "hoje" ? "bg-forest text-white" : "bg-background"}`}>Hoje</button>
                        <button type="button" onClick={() => setVetQuickWhen("agendar")} aria-pressed={vetQuickWhen === "agendar"} className={`flex-1 rounded-lg border px-3 py-3 ${vetQuickWhen === "agendar" ? "bg-forest text-white" : "bg-background"}`}>Agendar</button>
                      </div>
                    </div>
                  </div>
                  {vetQuickWhen === "agendar" && <label className="block text-sm font-semibold">Data do atendimento<input type="date" value={vetQuickDate} onChange={(e) => setVetQuickDate(e.target.value)} className="mt-1 block w-full rounded-lg border bg-background px-3 py-3 font-normal" /></label>}
                  <button type="button" disabled={vetQuickWhen === "agendar" && !vetQuickDate} onClick={buscarVeterinario} className="w-full rounded-lg bg-forest px-4 py-3 font-semibold text-white disabled:opacity-50">Encontrar veterinários</button>
                  <p className="text-xs text-muted-foreground">Os resultados não garantem atendimento imediato. Em emergências graves, procure um hospital veterinário 24 horas.</p>
                </section>
              ) : (
                <SearchBar onSearch={handleSearch} cidades={cidades} servicos={servicos} />
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full">
        {err ? (
          <div className="container mx-auto px-4 md:px-6 py-6 text-sm md:text-base">
            Erro ao carregar recomendações: {err}
          </div>
        ) : !data ? (
          <div className="container mx-auto px-4 md:px-6 py-6 text-sm md:text-base">
            Carregando recomendações…
          </div>
        ) : recomendacoes.length > 0 ? (
          <RecommendationCarousel listings={recomendacoes} />
        ) : (
          <div className="container mx-auto px-4 md:px-6 py-6 text-sm text-muted-foreground">
            Sem recomendações no momento.
          </div>
        )}
      </section>

      <section className="w-full">
        <TrustSections />
      </section>

      <section className="w-full">
        <FAQAccordion items={faqItems} />
      </section>

      <section className="w-full">
        <HostCTASection />
      </section>

      <Footer />
    </div>
  );
};

export default Index;